import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import {
  Archive, Loader2, X, AlertTriangle, Eye, EyeOff, FileText, Printer,
  ChevronDown, ChevronRight, Building2,
} from 'lucide-react';

// ===========================================================================
// JM Archive — the client write-ups this person already has
// ===========================================================================
//
// WHAT THIS IS
// ------------
// Before the ATS there was JM, and in JM every candidate sent to a client got
// two documents: an AI-written cover write-up and a redacted copy of their CV.
// 193 of those came across in the 31 Aug import and landed in
// `jm_resume_documents`, which nothing in the interface reads. This panel is
// the thing that reads it.
//
// FOUR RULES
// ----------
//
// 1. IT DISAPPEARS WHEN IT IS EMPTY. Roughly 1,800 of the 1,971 candidates have
//    nothing archived. For them this renders null — not an empty card, not a
//    "no records found" row. A panel that is present on every profile and
//    useful on one in ten is noise.
//
// 2. IT NEVER ALTERS THE DOCUMENT. What JM wrote is served back byte for byte.
//    These are the documents that actually went to a client, so a tidied-up
//    version would be a different document and useless as a record. The
//    documents render inside an iframe with scripts disabled, which also stops
//    a two-year-old stylesheet from redecorating the ATS around it.
//
// 3. IT WARNS BEFORE IT IS TRUSTED. JM's "redacted" CV is redacted by JM, not
//    by us. Each document is scanned server-side and, if an email address or a
//    phone number is still visible, the panel says so in the document's own
//    header — because the one thing that must not happen is somebody forwarding
//    one of these believing it is anonymous.
//
// 4. IT SHOWS WHAT THE CLIENT DID. JM recorded a view count per share link, so
//    each row says whether the client opened it and when. That is the single
//    most useful thing in the archive and it was invisible until now.

// House style. Copied rather than imported, same as the other new DCS panels.
const C = {
  cream: '#faf7f0', card: '#ffffff', border: '#e5e1d5',
  ink: '#2b2b2b', muted: '#7a7a7a', gold: '#c9a84c', goldDark: '#b08d36',
  navy: '#2e3a68', warn: '#8a6d1f', warnBg: '#fdf6e3',
};
const FONT = "'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const errText = (e) =>
  e?.response?.data?.detail || e?.message || 'Could not load the archive';

/** "Opened 4 times · last seen 18 Aug 2026", or the honest alternative. */
function shareLine(share) {
  if (!share || !share.links) return 'No share link recorded';
  if (!share.opened) return 'Sent, never opened';
  const times = share.views === 1 ? 'Opened once' : `Opened ${share.views} times`;
  return share.last_viewed_label ? `${times} · last ${share.last_viewed_label}` : times;
}

/** What a leak warning should say in words, not field names. */
function warnLine(kinds) {
  if (!kinds || !kinds.length) return null;
  if (kinds.length === 2) return 'This copy still shows an email address and a phone number.';
  if (kinds[0] === 'email') return 'This copy still shows an email address.';
  return 'This copy still shows a phone number.';
}

// ---------------------------------------------------------------------------
// The reader
// ---------------------------------------------------------------------------

function DocumentReader({ docId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('dossier');
  const frame = useRef(null);

  useEffect(() => {
    let alive = true;
    setDoc(null); setErr('');
    api.get(`/jm-archive/document/${encodeURIComponent(docId)}`)
      .then((r) => { if (alive) setDoc(r.data); })
      .catch((e) => { if (alive) setErr(errText(e)); });
    return () => { alive = false; };
  }, [docId]);

  // Esc closes, the way every other overlay in the ATS does.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The iframe carries no scripts, so it cannot report its own height. The
  // parent can read it because of allow-same-origin, which is the whole reason
  // that flag is set.
  const fit = useCallback(() => {
    const el = frame.current;
    try {
      const h = el?.contentDocument?.body?.scrollHeight;
      if (h) el.style.height = `${Math.min(h + 40, 20000)}px`;
    } catch { /* cross-origin should be impossible here, but never throw */ }
  }, []);

  const print = () => {
    try { frame.current?.contentWindow?.print(); } catch { /* blocked; ignore */ }
  };

  const has = (k) => (k === 'dossier' ? doc?.has_dossier : doc?.has_resume);
  const html = tab === 'dossier' ? doc?.dossier_html : doc?.resume_html;
  const warning = warnLine(doc?.warnings?.[tab]);

  const tabBtn = (key, label) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      disabled={!has(key)}
      style={{
        appearance: 'none', border: 'none', cursor: has(key) ? 'pointer' : 'not-allowed',
        background: tab === key ? C.card : 'transparent',
        color: !has(key) ? C.muted : tab === key ? C.ink : C.muted,
        font: `500 14px ${FONT}`, padding: '10px 18px', borderRadius: '8px 8px 0 0',
        borderBottom: tab === key ? `2px solid ${C.gold}` : '2px solid transparent',
        opacity: has(key) ? 1 : 0.5,
      }}
    >
      {label}{!has(key) && ' — not in the archive'}
    </button>
  );

  return (
    <div
      role="dialog" aria-modal="true" aria-label="Archived client document"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(30,28,24,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '24px 16px', overflowY: 'auto', fontFamily: FONT,
      }}
    >
      <div style={{
        background: C.cream, border: `1px solid ${C.border}`, borderRadius: 14,
        width: 'min(980px, 100%)', boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 18px 0',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: `600 16px ${FONT}`, color: C.ink }}>
              {doc?.candidate_name || 'Archived submission'}
            </div>
            <div style={{ font: `400 13px ${FONT}`, color: C.muted, marginTop: 2 }}>
              {[doc?.position, doc?.client].filter(Boolean).join(' · ') || 'Role not recorded'}
              {doc?.created_label ? ` · sent ${doc.created_label}` : ''}
              {doc?.doc_number ? ` · ${doc.doc_number}` : ''}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X size={16} />
          </Button>
        </div>

        <div style={{ padding: '12px 18px 0', display: 'flex', gap: 4 }}>
          {tabBtn('dossier', 'The write-up')}
          {tabBtn('resume', 'The CV we sent')}
          <div style={{ flex: 1 }} />
          <Button variant="outline" size="sm" onClick={print}
                  style={{ alignSelf: 'center' }}>
            <Printer size={14} style={{ marginRight: 6 }} />Print / save as PDF
          </Button>
        </div>

        <div style={{ padding: '0 18px 18px' }}>
          {warning && (
            <div style={{
              display: 'flex', gap: 8, alignItems: 'flex-start',
              background: C.warnBg, border: `1px solid ${C.gold}`,
              borderRadius: 10, padding: '10px 12px', margin: '10px 0 0',
              font: `400 13px ${FONT}`, color: C.warn,
            }}>
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{warning} Check it before you forward this to anyone.</span>
            </div>
          )}

          {err && (
            <div style={{ padding: 24, color: C.warn, font: `400 14px ${FONT}` }}>
              {err}
            </div>
          )}

          {!doc && !err && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: 32,
              color: C.muted, font: `400 14px ${FONT}`,
            }}>
              <Loader2 size={16} className="animate-spin" /> Opening the archive…
            </div>
          )}

          {doc && !err && (
            html ? (
              <iframe
                ref={frame}
                title="Archived document"
                srcDoc={html}
                onLoad={fit}
                sandbox="allow-same-origin allow-modals allow-popups"
                style={{
                  width: '100%', minHeight: 420, border: `1px solid ${C.border}`,
                  borderRadius: 10, background: C.card, marginTop: 10,
                }}
              />
            ) : (
              <div style={{
                padding: 32, textAlign: 'center', color: C.muted,
                font: `400 14px ${FONT}`, background: C.card, marginTop: 10,
                border: `1px solid ${C.border}`, borderRadius: 10,
              }}>
                This document was not part of the import.
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export default function JmArchivePanel({ candidateId, defaultOpen = true }) {
  const [items, setItems] = useState(null);   // null = still asking
  const [open, setOpen] = useState(defaultOpen);
  const [reading, setReading] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!candidateId) { setItems([]); return undefined; }
    let alive = true;
    setItems(null); setErr('');
    api.get(`/jm-archive/candidate/${encodeURIComponent(candidateId)}`)
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      // A failure here must not break the profile page around it. The panel
      // simply does not appear, exactly as it does for someone with no archive.
      .catch((e) => { if (alive) { setErr(errText(e)); setItems([]); } });
    return () => { alive = false; };
  }, [candidateId]);

  // Rule 1: nothing archived, nothing rendered.
  if (items === null || items.length === 0 || err) return null;

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      fontFamily: FONT, marginTop: 16, overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', background: C.cream, border: 'none',
          borderBottom: open ? `1px solid ${C.border}` : 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {open ? <ChevronDown size={16} color={C.muted} />
              : <ChevronRight size={16} color={C.muted} />}
        <Archive size={16} color={C.goldDark} />
        <span style={{ font: `600 15px ${FONT}`, color: C.ink }}>
          Past client submissions
        </span>
        <span style={{
          font: `600 12px ${FONT}`, color: C.goldDark, background: '#fdf6e3',
          border: `1px solid ${C.gold}`, borderRadius: 999, padding: '2px 9px',
        }}>
          {items.length}
        </span>
        <span style={{ font: `400 13px ${FONT}`, color: C.muted, marginLeft: 'auto' }}>
          from the old system
        </span>
      </button>

      {open && (
        <div>
          {items.map((it, i) => (
            <div key={it.id} style={{
              display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 16px',
              borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: `600 14px ${FONT}`, color: C.ink }}>
                  {it.position || 'Role not recorded'}
                </div>
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 3,
                  font: `400 13px ${FONT}`, color: C.muted, alignItems: 'center',
                }}>
                  {it.client && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Building2 size={13} />{it.client}
                    </span>
                  )}
                  {it.created_label && <span>Sent {it.created_label}</span>}
                  {it.doc_number && (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{it.doc_number}</span>
                  )}
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    color: it.share?.opened ? C.navy : C.muted,
                  }}>
                    {it.share?.opened ? <Eye size={13} /> : <EyeOff size={13} />}
                    {shareLine(it.share)}
                  </span>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setReading(it.id)}
                      style={{ flexShrink: 0 }}>
                <FileText size={14} style={{ marginRight: 6 }} />Open
              </Button>
            </div>
          ))}
        </div>
      )}

      {reading && (
        <DocumentReader docId={reading} onClose={() => setReading(null)} />
      )}
    </div>
  );
}
